// High-value CTA instrumentation via a single delegated click listener on
// `document`. We intentionally avoid per-element binding — one capture-phase
// listener covers tel:, external links, and our own `data-fintepla-cta="…"`
// hooks without adding noise to every trivial DOM element.

import { newClientActionId, track } from './index.js'

const INIT_FLAG = '__finteplaCtaDelegationInitialized'

function getHost() {
  try {
    return window.location.host
  } catch {
    return ''
  }
}

function handleClick(event) {
  const target = event.target
  if (!target || typeof target.closest !== 'function') return

  const tel = target.closest('a[href^="tel:"]')
  if (tel) {
    track('phone_cta_clicked', {
      correlation: { client_action_id: newClientActionId() },
      message: {
        button_label: (tel.textContent || '').trim().slice(0, 120) || undefined,
      },
      metadata: { href: tel.getAttribute('href') || undefined },
    })
    return
  }

  const anchor = target.closest('a[href]')
  if (anchor) {
    const href = anchor.getAttribute('href') || ''
    let externalHost = null
    try {
      const u = new URL(href, window.location.href)
      if (
        (u.protocol === 'http:' || u.protocol === 'https:') &&
        u.host &&
        u.host !== getHost()
      ) {
        externalHost = u.host
      }
    } catch {
      // ignore malformed
    }
    if (externalHost || anchor.target === '_blank') {
      track('external_link_clicked', {
        correlation: { client_action_id: newClientActionId() },
        message: {
          button_label:
            (anchor.textContent || '').trim().slice(0, 120) || undefined,
        },
        metadata: { href, host: externalHost || undefined },
      })
      return
    }
  }
}

/** Install the delegated listener exactly once per page. */
export function installCtaDelegation() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (window[INIT_FLAG]) return
  window[INIT_FLAG] = true
  try {
    document.addEventListener('click', handleClick, { capture: true })
  } catch {
    // swallow — analytics must never break the page
  }
}

